import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/forgot-password")({
  head: () => ({
    meta: [
      { title: "Reset your password — KnowMeno" },
      { name: "description", content: "Reset your KnowMeno password." },
    ],
  }),
  component: ForgotPasswordPage,
});

const RO = "#D4537E";
const GR = "#888780";
const PLUM = "#4A1942";
const WH = "#ffffff";

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (err) throw err;
      setSent(true);
    } catch (err: any) {
      setError(err.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(165deg, #FBFAF6 0%, #F7EEEA 60%, #F2E1DA 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
        fontFamily: '"Cormorant Garamond", Georgia, serif',
      }}
    >
      <div
        style={{
          maxWidth: 440,
          width: "100%",
          background: WH,
          borderRadius: 24,
          padding: "36px 32px",
          boxShadow: "0 30px 80px -30px rgba(74, 25, 66, 0.25)",
        }}
      >
        <Link
          to="/sign-in"
          style={{
            color: GR,
            fontSize: 13,
            textDecoration: "none",
            fontFamily: "system-ui, sans-serif",
            display: "inline-block",
            marginBottom: 18,
          }}
        >
          ← Back to sign in
        </Link>
        <div
          style={{
            fontSize: 30,
            fontWeight: 600,
            letterSpacing: "-0.01em",
            display: "flex",
            alignItems: "baseline",
            marginBottom: 10,
            fontFamily: "system-ui, sans-serif",
          }}
        >
          <span style={{ color: RO }}>Meno</span>
          <span style={{ color: GR, fontWeight: 300, margin: "0 1px" }}>-</span>
          <span style={{ color: PLUM }}>IQ</span>
        </div>
        <h1
          style={{
            fontSize: 34,
            lineHeight: 1.1,
            color: "#2A1228",
            marginBottom: 8,
            fontWeight: 500,
            letterSpacing: "-0.02em",
          }}
        >
          Reset your password
        </h1>
        <p
          style={{
            color: GR,
            fontSize: 15,
            lineHeight: 1.55,
            marginBottom: 26,
            fontFamily: "system-ui, sans-serif",
          }}
        >
          {sent
            ? "Check your inbox for a reset link. It expires in 60 minutes."
            : "Enter the email on your account and we'll send a link to set a new password."}
        </p>

        {!sent ? (
          <form
            onSubmit={handleSubmit}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              fontFamily: "system-ui, sans-serif",
            }}
          >
            <input
              type="email"
              required
              autoComplete="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                border: "1px solid #E0DED7",
                borderRadius: 14,
                padding: "14px 16px",
                fontSize: 15,
                fontFamily: "inherit",
                outline: "none",
                background: WH,
              }}
            />
            {error && (
              <div style={{ color: RO, fontSize: 13 }}>{error}</div>
            )}
            <button
              type="submit"
              disabled={submitting}
              style={{
                background: PLUM,
                color: WH,
                border: "none",
                borderRadius: 14,
                padding: 15,
                fontSize: 15,
                fontWeight: 500,
                cursor: submitting ? "default" : "pointer",
                opacity: submitting ? 0.6 : 1,
                marginTop: 6,
                fontFamily: "system-ui, sans-serif",
              }}
            >
              {submitting ? "Sending…" : "Send reset link"}
            </button>
          </form>
        ) : (
          <button
            onClick={() => navigate({ to: "/sign-in" })}
            style={{
              background: PLUM,
              color: WH,
              border: "none",
              borderRadius: 14,
              padding: 15,
              fontSize: 15,
              fontWeight: 500,
              cursor: "pointer",
              fontFamily: "system-ui, sans-serif",
              width: "100%",
            }}
          >
            Back to sign in
          </button>
        )}
      </div>
    </div>
  );
}
